<template>
  <bg-style
    v-if="enabled"
    class="store-widget-v4"
    :is-disabled="$store.getters.isStandAloneMode"
    :class="{ 'is-mobile': isMobile }"
    :bg="model.background"
    :style="getStyle"
  >
    <module-block :bg="model.background">
      <div v-if="model.titleVisible" class="title-box">
        <rich-text
          v-model="model.title"
          :disabled="!editing"
          :editing="editing"
          class="w-full relative"
          placement="bottom"
          theme="snow"
        />
      </div>

      <div class="store-widget-v4__carousel">
        <button
          v-if="isMobile && list.length > 1"
          class="carousel-arrow prev"
          :class="{ 'has-img': switchIconUrl }"
          @click="goTo(current - 1)"
        >
          <img v-if="switchIconUrl" :src="switchIconUrl" alt="" />
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          v-if="isMobile && list.length > 1"
          class="carousel-arrow next"
          :class="{ 'has-img': switchIconUrl }"
          @click="goTo(current + 1)"
        >
          <img v-if="switchIconUrl" :src="switchIconUrl" alt="" />
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        <div ref="track" class="store-widget-v4__track" @scroll="onScroll">
          <goods-item
            v-for="item in list"
            :key="item.id"
            :good="item"
            :model="model"
            :editing="editing"
            :site="site"
            @buy="handleBuy"
          />
        </div>

        <div v-if="isMobile && list.length > 1" class="carousel-dots">
          <button
            v-for="(item, i) in list"
            :key="i"
            class="carousel-dot"
            :class="{ active: i === current }"
            @click="goTo(i)"
          />
        </div>
      </div>

      <div v-if="model.describeVisible" class="describe-box">
        <rich-text
          v-model="model.describe"
          :disabled="!editing"
          :editing="editing"
          class="describe relative"
          placement="bottom"
          theme="snow"
        />
      </div>
    </module-block>
    <desk-pay-modal
      v-if="deskPay.visible"
      :desk-pay="deskPay"
      :lang-id-data="$store.state.locale.langIdData"
      @close="closePay"
    />
  </bg-style>
</template>

<script>
import { Message } from 'element-ui'
import { cloneDeep } from 'lodash-es'
import GoodsItem from './item.vue'
import ModuleBlock from '~/components/common/ModuleBlock.vue'
import {
  checkIgnoreMessage,
  checkUser,
  generateOrderParams,
  generatePayUrl,
  getLangIdI18n,
  getTextColor,
} from '~/utils'
import richText from '~/components/richText/index.vue'
import DeskPayModal from '~/components/pay/deskPayModal.vue'
import Bus from '~/site/model/bus'

export default {
  components: {
    DeskPayModal,
    richText,
    ModuleBlock,
    GoodsItem,
  },
  props: {
    model: Object,
    editing: Boolean,
    goods: Object,
    site: Object,
  },
  data() {
    return {
      enabled: false,
      current: 0,
      list: [],
      loading: false,
      scrollTimer: null,
      deskPay: {
        visible: false,
        isGoPay: false,
        payUrl: '',
        orderCode: '',
      },
    }
  },
  computed: {
    isMobile() {
      return this.$store.getters.isMobile
    },
    getStyle() {
      return {
        '--text-color': getTextColor(this.model.textColor).color,
        '--card-bg': getTextColor(this.model.cardBackground).color,
        '--button-bg': getTextColor(this.model.buttonBackground).color,
        '--button-color': getTextColor(this.model.buttonColor).color,
      }
    },
    switchIconUrl() {
      return this.model.switchIcon?.backgroundImage
    },
  },
  watch: {
    'model.classId'(n, o) {
      if (n !== o) {
        this.getGoodsById()
      }
    },
  },
  mounted() {
    this.init()
    Bus.$on('reloadGood', (type) => {
      const isLogin = type === 'login'
      if (isLogin) {
        this.checkBuy()
      }
    })
  },
  beforeDestroy() {
    if (this.scrollTimer) clearTimeout(this.scrollTimer)
  },
  methods: {
    checkEnabled() {
      if (this.editing) {
        this.enabled = true
      } else {
        const has = Boolean(this.list?.length)
        this.enabled = has
        this.model.__hide = !has
      }
    },
    checkBuy() {
      const isAim = this.awaitGoods && this.awaitGoods.modelId === this.model.id
      if (isAim) this.createOrder(this.awaitGoods.data)
    },
    closePay() {
      this.deskPay = {
        visible: false,
        isGoPay: false,
        payUrl: '',
        orderCode: '',
      }
    },
    async init() {
      try {
        await this.$store.dispatch('goods/getGroupList', this)
        await this.getGoodsById()
      } finally {
        this.$widgetReady()
      }
    },
    async getGoodsById() {
      if (!this.model.classId && this.editing) {
        this.list = this.model.__mockData
        this.current = 0
        this.checkEnabled()
        return
      } else if (!this.model.classId) {
        this.checkEnabled()
        return
      }
      const params = {
        pageSize: 200,
        pageNo: 1,
        group_id: this.model.classId,
        project_id: this.$store.state.project.projectId,
      }
      const currency = this.$store.state.user?.ipInfo?.currency
      const langIdData = this.$store.state.locale.langIdData
      const [err, res] = await this.$utils.to(this.$api.good.getAllGoodsList(params, this.editing))
      if (!err) {
        const discountInfo = res.data?.[0]?.discount_info
        if (discountInfo?.discount_level) {
          this.$store.commit('goods/SET_DISCOUNT_INFO', cloneDeep(discountInfo))
        }
        this.list = (res.data?.[0]?.data || []).map((item) => {
          const newItem = { ...item }
          // 名称：lang_id → 文案
          newItem.names = {
            name: getLangIdI18n({ lang_id: item.name }, langIdData),
            desc: getLangIdI18n({ lang_id: item.description }, langIdData),
          }
          // 道具名称（数量 num 直接字段）
          newItem.item_contents = (newItem.item_contents || []).map((contentItem) => {
            contentItem.name = getLangIdI18n({ lang_id: contentItem.name }, langIdData)
            return contentItem
          })
          // item_contents2 数量按币种取值，过滤 0 项
          newItem.item_contents2 = (newItem.item_contents2 || []).reduce((total, contentItem) => {
            contentItem.name = getLangIdI18n({ lang_id: contentItem.name }, langIdData)
            contentItem.num = contentItem.num_by_currency[currency]
            contentItem.num && total.push(contentItem)
            return total
          }, [])
          newItem.system_items = (newItem.system_items || []).map((sysItem) => {
            sysItem.name = getLangIdI18n({ lang_id: sysItem.name }, langIdData)
            return sysItem
          })
          return newItem
        })
        this.current = 0
        this.checkEnabled()
      } else {
        Message.error(err)
      }
    },
    async createOrder(goods) {
      this.loading = true
      const [err, res] = await this.$utils.to(generateOrderParams(this, { ...goods, num: 1 }))
      this.loading = false
      if (!err) {
        this.$store.commit('site/SET_AWAIT_GOODS', null)
        generatePayUrl(res, this)
      } else {
        !checkIgnoreMessage(err) && Message.error(err)
      }
    },
    async handleBuy(goods) {
      if (this.editing || this.loading) return
      await checkUser(this, () => {
        this.$store.commit('site/SET_AWAIT_GOODS', { modelId: this.model.id, data: cloneDeep(goods) })
      })
      await this.createOrder(goods)
    },
    goTo(index) {
      if (!this.isMobile) return
      const total = this.list.length
      if (index < 0) index = 0
      if (index >= total) index = total - 1
      this.current = index
      const track = this.$refs.track
      const card = track?.querySelectorAll('.gift-card')[index]
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
      }
    },
    onScroll() {
      if (!this.isMobile) return
      if (this.scrollTimer) clearTimeout(this.scrollTimer)
      this.scrollTimer = setTimeout(() => {
        const track = this.$refs.track
        if (!track) return
        const cards = track.querySelectorAll('.gift-card')
        let nearest = 0
        let minDist = Infinity
        cards.forEach((card, i) => {
          const offset = card.offsetLeft - track.offsetLeft
          const dist = Math.abs(offset - track.scrollLeft)
          if (dist < minDist) {
            minDist = dist
            nearest = i
          }
        })
        if (nearest !== this.current) {
          this.current = nearest
        }
      }, 80)
    },
  },
}
</script>

<style lang="less">
.store-widget-v4 {
  padding: 20px 0;

  .title-box {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 16px;
  }

  .describe-box {
    max-width: 1200px;
    margin: 20px auto 0 auto;
    padding: 0 16px;

    .describe {
      width: 100%;
    }
  }

  &__carousel {
    position: relative;
    max-width: 1200px;
    margin: 20px auto 0 auto;
    padding: 0 16px;
  }

  // 桌面端：一行 3 个
  &__track {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }

  .carousel-arrow,
  .carousel-dots {
    display: none;
  }

  // 移动端：一行一个轮播
  &.is-mobile {
    padding: 10px 0;

    .store-widget-v4__carousel {
      padding: 0;
    }

    .store-widget-v4__track {
      display: flex;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      -ms-overflow-style: none;
      gap: 12px;
      padding: 0 20px;

      &::-webkit-scrollbar {
        display: none;
      }

      .gift-card {
        flex: 0 0 100%;
        scroll-snap-align: center;
      }
    }

    .carousel-arrow {
      position: absolute;
      top: 50%;
      z-index: 10;
      width: 36px;
      height: 36px;
      border: none;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.05);
      backdrop-filter: blur(8px);
      color: #5f6368;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transform: translateY(-50%);
      padding: 0;

      svg {
        width: 18px;
        height: 18px;
      }

      img {
        width: 36px;
        height: 36px;
        object-fit: contain;
      }

      &.has-img {
        width: auto;
        height: auto;
        background: transparent;
        border-radius: 0;
        backdrop-filter: none;
      }

      &.prev img {
        transform: scaleX(-1);
      }

      &.prev {
        left: -6px;
      }
      &.next {
        right: -6px;
      }
    }

    .carousel-dots {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 12px 0 4px;
    }

    .carousel-dot {
      width: 6px;
      height: 6px;
      border-radius: 3px;
      background: rgba(0, 0, 0, 0.15);
      border: none;
      padding: 0;
      cursor: pointer;
      transition: all 0.3s;

      &.active {
        width: 20px;
        background: var(--button-bg);
      }
    }
  }
}
</style>
